import React, { useState } from 'react';
import 'iconify-icon';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { PROGRAMS, MODALITIES } from './agenda-shared';

const FilterSelect = ({ label, value, onChange, options, disabled, testId }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5 block">
      {label}
    </span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testId}
        className={`w-full appearance-none bg-[#1A1A1A] border border-[#333333] text-[#EAEAEA] rounded-xl px-3 py-3 pr-8 text-sm focus:outline-none focus:border-accent-orange transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent-orange'
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#1A1A1A] text-[#EAEAEA]">
            {o.label}
          </option>
        ))}
      </select>
      <iconify-icon
        icon="solar:alt-arrow-down-linear"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
      ></iconify-icon>
    </div>
  </label>
);

const FilterDate = ({ label, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const displayLabel = selected
    ? format(selected, "dd 'de' MMM, yyyy", { locale: ptBR })
    : 'Selecione';

  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5 block">
        {label}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className="w-full flex items-center justify-between gap-2 bg-[#1A1A1A] border border-[#333333] text-[#EAEAEA] rounded-xl px-3 py-3 text-sm focus:outline-none hover:border-accent-orange focus:border-accent-orange transition-colors"
          >
            <span className={selected ? '' : 'text-neutral-500'}>{displayLabel}</span>
            <iconify-icon icon="solar:calendar-mark-linear" className="text-neutral-500 text-base flex-shrink-0"></iconify-icon>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0F0F0F] border border-[#333333] text-[#EAEAEA]" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              onChange(d ? format(d, 'yyyy-MM-dd') : '');
              setOpen(false);
            }}
            locale={ptBR}
            initialFocus
            className="bg-transparent"
            classNames={{
              caption_label: 'text-sm font-medium text-[#EAEAEA] capitalize',
              head_cell: 'text-neutral-500 rounded-md w-8 font-mono text-[10px] uppercase',
              day: 'h-8 w-8 p-0 font-normal text-[#EAEAEA] hover:bg-[#1A1A1A] rounded-md transition-colors',
              day_selected:
                'bg-accent-orange text-white hover:bg-[#E55D00] hover:text-white focus:bg-accent-orange focus:text-white',
              day_today: 'text-accent-orange font-bold',
              day_outside: 'text-neutral-600',
              day_disabled: 'text-neutral-700 opacity-50',
              nav_button: 'h-7 w-7 bg-transparent text-[#EAEAEA] hover:bg-[#1A1A1A] rounded-md transition-colors',
            }}
          />
          {selected && (
            <div className="border-t border-[#333333] p-2 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-accent-orange px-3 py-1.5"
              >
                Limpar
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </label>
  );
};

export const FiltersBar = ({
  program, setProgram,
  modality, setModality,
  stateCode, setStateCode,
  city, setCity,
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  availableStates, availableCities,
  onApply, onClear,
}) => (
  <section
    className="sticky top-16 z-30 border-b border-[#333333] backdrop-blur-md"
    style={{ background: 'rgba(17, 17, 17, 0.96)' }}
    data-testid="agenda-filters-bar"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end">
        <FilterSelect
          testId="filter-program"
          label="Programa"
          value={program}
          onChange={setProgram}
          options={[
            { value: '', label: 'Todos' },
            ...PROGRAMS.map((p) => ({ value: p.slug, label: p.name })),
          ]}
        />
        <FilterSelect
          testId="filter-modality"
          label="Modalidade"
          value={modality}
          onChange={setModality}
          options={MODALITIES}
        />
        <FilterSelect
          testId="filter-state"
          label="Estado"
          value={stateCode}
          onChange={(v) => {
            setStateCode(v);
            setCity('');
          }}
          options={[
            { value: '', label: 'Todos' },
            ...availableStates.map((s) => ({ value: s.code, label: s.code })),
          ]}
        />
        <FilterSelect
          testId="filter-city"
          label="Cidade"
          value={city}
          onChange={setCity}
          disabled={!stateCode}
          options={[
            {
              value: '',
              label: !stateCode
                ? '— Selecione um estado'
                : availableCities.length === 0
                  ? 'Sem cidades disponíveis'
                  : 'Todas',
            },
            ...availableCities.map((c) => ({ value: c, label: c })),
          ]}
        />
        <FilterDate testId="filter-date-from" label="De" value={dateFrom} onChange={setDateFrom} />
        <FilterDate testId="filter-date-to" label="Até" value={dateTo} onChange={setDateTo} />
        <div className="col-span-2 md:col-span-1 flex flex-col gap-1">
          <span className="invisible text-[10px] font-mono uppercase tracking-[0.25em]">.</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onApply}
              data-testid="filter-apply"
              className="flex-1 px-4 py-3 bg-accent-orange text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
            >
              Filtrar
            </button>
            <button
              type="button"
              onClick={onClear}
              data-testid="filter-clear"
              className="px-3 py-3 border border-[#333333] text-neutral-400 rounded-xl text-xs font-mono uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors"
              title="Limpar filtros"
            >
              <iconify-icon icon="solar:close-circle-linear"></iconify-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);
